import React, { useEffect, useState } from "react";
import { CloudOff, RefreshCw, AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  subscribePending,
  flushOutbox,
  listFailedEntries,
  discardOutboxEntry,
} from "../lib/offline-outbox";
import api from "../lib/api";

/**
 * Persistent badge that surfaces the offline outbox state and lets the user
 * act on it:
 *   - Pending items: click to trigger a manual sync (the auto-sync on
 *     `online` event still fires; this is the "try again now" affordance).
 *   - Failed items (4xx replays or replays that exhausted MAX_REPLAY_ATTEMPTS):
 *     OFF-6 of the 2026-05-27 audit. Without a UI surface these items would
 *     live in IDB forever, invisible to the user — the badge counter would
 *     never drop to zero and the user would have no way to discard them.
 *
 * Returns null when both counters are zero so the badge stays out of the
 * header chrome during normal operation. Wired into BottomNav / Layout.
 */
export default function OfflineSyncBadge() {
  const { t } = useTranslation();
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [showFailedPanel, setShowFailedPanel] = useState(false);
  const [failedEntries, setFailedEntries] = useState([]);

  useEffect(
    () =>
      subscribePending((counts) => {
        if (typeof counts === "number") {
          // Defensive: legacy subscribers used a numeric `count` callback.
          setPending(counts);
          setFailed(0);
        } else {
          setPending(counts?.pending || 0);
          setFailed(counts?.failed || 0);
        }
      }),
    []
  );

  useEffect(() => {
    if (!showFailedPanel) return;
    let alive = true;
    listFailedEntries().then((entries) => {
      if (alive) setFailedEntries(entries);
    });
    return () => {
      alive = false;
    };
  }, [showFailedPanel, failed]);

  if (pending === 0 && failed === 0) return null;

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await flushOutbox(api);
    } finally {
      setSyncing(false);
    }
  };

  const handleDiscard = async (id) => {
    await discardOutboxEntry(id);
  };

  return (
    <div className="flex items-center gap-1.5 relative">
      {pending > 0 && (
        <button
          onClick={handleSync}
          className="flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 px-2.5 py-1 text-[11px] font-semibold text-amber-100 hover:bg-amber-500/25 transition-colors"
          title={t("offline.pendingTooltip")}
        >
          {syncing ? (
            <RefreshCw className="w-3 h-3 animate-spin" />
          ) : (
            <CloudOff className="w-3 h-3" />
          )}
          <span>
            {pending} {pending === 1 ? t("offline.pendingOne") : t("offline.pendingMany")}
          </span>
        </button>
      )}
      {failed > 0 && (
        <button
          onClick={() => setShowFailedPanel((v) => !v)}
          className="flex items-center gap-1.5 rounded-full bg-red-500/15 border border-red-500/40 px-2.5 py-1 text-[11px] font-semibold text-red-100 hover:bg-red-500/25 transition-colors"
          title={t("offline.failedTooltip")}
        >
          <AlertTriangle className="w-3 h-3" />
          <span>
            {failed} {failed === 1 ? t("offline.failedOne") : t("offline.failedMany")}
          </span>
        </button>
      )}
      {showFailedPanel && failedEntries.length > 0 && (
        <div className="absolute top-full right-0 mt-1 w-72 rounded-xl bg-neutral-900/95 border border-red-500/30 shadow-xl backdrop-blur-sm p-2 z-50">
          <div className="text-[11px] font-semibold text-red-200 px-1 pb-1.5">
            {t("offline.failedHeading")}
          </div>
          <ul className="flex flex-col gap-1 max-h-64 overflow-y-auto">
            {failedEntries.map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between gap-2 text-[11px] text-neutral-200 bg-neutral-800/60 rounded-lg px-2 py-1.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{e.summary || e.url}</div>
                  {e.last_error && (
                    <div className="text-[10px] text-neutral-400 truncate">{e.last_error}</div>
                  )}
                </div>
                <button
                  onClick={() => handleDiscard(e.id)}
                  className="flex-shrink-0 p-1 rounded hover:bg-red-500/20 text-red-200"
                  title={t("offline.failedDiscard")}
                >
                  <X className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
